import { Member } from '@aid/members/types/classes/member.class';
import { Gender } from '@aid/shared/types/enums/gender.enum';
import { Marital } from '@aid/customers/types/enums/marital.enum';
import { CustomerSpecification } from '@aid/customers/types/enums/customer-specification.enum';
import { TypeOfCustomer } from '@aid/customers/types/enums/type-of-customer.enum';
import { CustomerContract } from '@aid/customers/types/classes/customer-contract.class';
import { CustomerLegalForm } from '@aid/customers/types/classes/customer-legal-form.class';
import {
  CustomerAdditionalInformation,
  CustomerContactInformation
} from '@aid/customers/types/interfaces';

export class Client {
  id?: number;
  profilePicture?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  type?: TypeOfCustomer;
  companyName?: string;
  legalForm?: number | CustomerLegalForm;
  street?: string;
  houseNumber?: string;
  country?: string;
  city?: string;
  postcode?: string;
  gender?: Gender;
  birthday?: string | null;
  email?: string;
  phoneNumber?: string;
  phoneNumberSecondary?: string;
  taxId?: string;
  maritalStatus?: Marital;
  publicService?: boolean;
  selfEmployed?: boolean;
  selfEmployedCompanyName?: string;
  job?: string;

  /**
   * Optional fields
   */
  title?: string;
  nameSuffix?: string;
  nationality?: string;
  placeOfBirth?: string;
  birthName?: string;
  idCardType?: string;
  idNumber?: string;
  // The optional-information form clears these with null.
  idStartDate?: Date | string | null;
  idEndDate?: Date | string | null;
  idCardAuthority?: string;
  remarks?: string;
  vat?: number;
  registryNumber?: number;
  registrationDate?: string;
  activityDomain?: string;

  /**
   * If the client has an user, then it has an account
   */
  user?: number;
}

export class Customer {
  id?: number;

  /**
   * A customer is a relationship between a client and a Broker
   */
  supervisor?: Member | number;
  partnershipResponsible?: Member;
  client?: Client;

  immediateAccess?: boolean;
  customerSpecifications?: CustomerSpecification | string;
  customerNumber?: string;
  invitation?: boolean;

  /**
   * Optional fields
   */
  contract?: CustomerContract | null;

  set contactInformation(information: CustomerContactInformation) {
    const client = requireCustomerClient(this);
    client.firstName = information.firstName;
    client.lastName = information.lastName;
    client.email = information.email;
    client.gender = information.gender;
    client.birthday = information.birthday;
    client.phoneNumber = information.phoneNumber;
    client.phoneNumberSecondary = information.phoneNumberSecondary;
    client.maritalStatus = information.maritalStatus;
    client.job = information.job;
  }

  set additionalInformation(information: CustomerAdditionalInformation) {
    this.customerSpecifications = information.customerSpecifications;
    this.immediateAccess = information.immediateAccess;
    const client = requireCustomerClient(this);
    client.taxId = information.taxId;
    client.publicService = information.publicService;
    client.selfEmployed = information.selfEmployed;
    client.selfEmployedCompanyName = information.selfEmployedCompanyName;
  }
}

/**
 * A function, not a Customer method, because component inputs are often plain
 * API objects without the methods.
 */
export function requireCustomerClient(
  customer: Pick<Customer, 'client'>
): Client {
  if (!customer.client) {
    throw new Error('The customer has no client details.');
  }
  return customer.client;
}

/**
 * The app user id of a customer's client. CustomerChatDetailsButtonsComponent
 * disables the chat for clients without an app account.
 */
export function requireCustomerUser(
  customer: Pick<Customer, 'client'>
): number {
  const user: number | null | undefined = requireCustomerClient(customer).user;
  if (user === undefined || user === null) {
    throw new Error('The customer has no app account.');
  }
  return user;
}
